import React, { useEffect, useMemo, useState } from "react";
import {
  evidenceElements, claimTheoryOptions, bodySystems, getConditionResources, conditionLibrary,
} from "@/lib/claimData";
import { getSecondarySuggestions } from "@/lib/secondaryConditions";
import { uid, computePillarStatus } from "@/lib/claimUtils";
import { nexusWaiver } from "@/lib/nexusWaiver";
import { analyzeIssueRisk } from "@/lib/riskEngine";
import {
  getRatingTierSummary, getEvidencePresenceSummary, detectClaimRisks,
  previewCombined, buildPasteable526EZLanguage,
} from "@/lib/claimGuidance";
import { listSymptoms } from "@/lib/api";
import { isStaleEvidence, STALE_EVIDENCE_MONTHS } from "@/lib/reminderUtils";
import { THEORY_SHORT_LABEL } from "@/lib/claimEnums";
import CoOccurrenceHints from "@/components/CoOccurrenceHints";
import SimpleConditionDetail from "@/components/SimpleConditionDetail";
import { PILLAR_STATE, PILLAR_LABEL, PILLAR_CLASS, rollupIssueState } from "@/lib/pillarStatusV2";
import {
  Trash, Tag, CaretDown, CaretUp, ArrowSquareOut, Stethoscope, ClipboardText, CheckSquare,
  Plus, Lightning, Link, FileText, Vault, ListBullets, SquaresFour, Paperclip,
  MagnifyingGlass, ArrowsDownUp, Funnel, Compass, ArrowLeft, WarningCircle, Copy,
  ShieldWarning, CheckCircle, X,
} from "@phosphor-icons/react";

const STATUS_CLASS = { have: "status--strong", need: "status--missing" };
const STATUS_LABEL = { have: "Have it", need: "Need it" };

// Body-system color palette — gives every system a distinct visual identity in the navigator
const SYSTEM_COLORS = {
  musculoskeletal: "#DC2626",   // red
  mental:          "#7C3AED",   // violet
  respiratory:     "#0EA5E9",   // sky
  neurological:    "#F59E0B",   // amber
  hearing:         "#10B981",   // emerald
  digestive:       "#D97706",   // dark orange
  skin:            "#EC4899",   // pink
  cardio:          "#E11D48",   // rose
  genitourinary:   "#0891B2",   // cyan
  dental:          "#94A3B8",   // slate
  endocrine:       "#A855F7",   // purple
  hemic:           "#BE123C",   // deep red
  eye:             "#2563EB",   // blue
  gynecological:   "#DB2777",   // hot pink
  infectious:      "#65A30D",   // lime
};
const colorFor = (sysId) => SYSTEM_COLORS[sysId] || "#64748B";

const PRIORITY_RANK = { High: 0, Medium: 1, Low: 2 };
const priorityRank = (p) => (PRIORITY_RANK[p] ?? PRIORITY_RANK.Medium);

function issueReadinessPct(issue, evidence, ctx) {
  const have = evidenceElements.filter((el) => computePillarStatus(issue, el.id, evidence, ctx) === "have").length;
  return Math.round((have / evidenceElements.length) * 100);
}

function issueOverallStatus(issue, evidence, ctx) {
  // "have" if any pillar has evidence attached; otherwise "need"
  for (const el of evidenceElements) {
    if (computePillarStatus(issue, el.id, evidence, ctx) === "have") return "have";
  }
  return "need";
}

function EvidenceItemRow({ ev, onUpdate, onDelete, showElement = false }) {
  const stale = isStaleEvidence(ev);
  return (
    <div className={`evidence-item-compact ${stale ? "evidence-item-compact--stale" : ""}`} data-testid={`evidence-item-${ev.id}`}>
      {stale && (
        <div className="ev-stale-badge" data-testid={`ev-stale-${ev.id}`} title={`Evidence is older than ${STALE_EVIDENCE_MONTHS} months — VA may want updated records`}>
          <WarningCircle size={11} weight="fill" /> STALE ({STALE_EVIDENCE_MONTHS}+ MONTHS)
        </div>
      )}
      <div className="ev-row-top">
        <input
          className="field-input ev-title-input"
          value={ev.title || ""}
          onChange={(e) => onUpdate(ev.id, { title: e.target.value })}
          placeholder="Evidence title"
          data-testid={`ev-title-${ev.id}`}
        />
        {showElement && (
          <select
            className="field-input ev-element-select"
            value={ev.element || "current"}
            onChange={(e) => onUpdate(ev.id, { element: e.target.value })}
            data-testid={`ev-element-${ev.id}`}
          >
            {evidenceElements.map((el) => (
              <option key={el.id} value={el.id}>{el.label}</option>
            ))}
          </select>
        )}
        <button className="btn btn--danger btn--sm" onClick={() => onDelete(ev.id)} data-testid={`delete-evidence-${ev.id}`}>
          <Trash size={12} weight="bold" />
        </button>
      </div>
      <div className="ev-row-bottom">
        <input
          className="field-input ev-source-input"
          value={ev.source || ""}
          onChange={(e) => onUpdate(ev.id, { source: e.target.value })}
          placeholder="Source (STR, VA record, private...)"
        />
        <input
          className="field-input ev-date-input mono"
          type="date"
          value={ev.date || ""}
          onChange={(e) => onUpdate(ev.id, { date: e.target.value })}
        />
        <textarea
          className="field-input ev-summary-input"
          rows={2}
          value={ev.summary || ""}
          onChange={(e) => onUpdate(ev.id, { summary: e.target.value })}
          placeholder="What does this evidence show?"
        />
      </div>
    </div>
  );
}

function NavigatorCard({ issue, evidence, isActive, onClick, waiverCtx }) {
  const stripe = colorFor(issue.system);
  const sysLabel = bodySystems.find((s) => s.id === issue.system)?.label || issue.system;
  const officialName = conditionLibrary.find((c) => c.code === issue.code)?.name || "";
  const stateV2 = rollupIssueState(issue, evidence, waiverCtx);
  const issueEv = evidence.filter((ev) => ev.issueIds?.includes(issue.id));
  const evCount = issueEv.length;
  const staleCount = issueEv.filter((ev) => isStaleEvidence(ev)).length;
  const primary = issue.claimedAs?.trim() || officialName || "(untitled)";
  const secondary = officialName && officialName.toLowerCase() !== primary.toLowerCase() ? officialName : "";
  return (
    <button
      type="button"
      className={`nav-card nav-card--v2 ${isActive ? "nav-card--active" : ""}`}
      onClick={onClick}
      data-testid={`nav-card-${issue.id}`}
      style={{ "--nav-stripe": stripe }}
      title={`${primary}${secondary ? " — " + secondary : ""} (DC ${issue.code} • ${sysLabel})`}
    >
      <span className="nav-card-stripe" aria-hidden="true" />
      <span className="nav-card-body">
        <span className="nav-card-title" data-testid={`nav-card-title-${issue.id}`}>{primary}</span>
        {secondary && (
          <span className="nav-card-condname" data-testid={`nav-card-condname-${issue.id}`} style={{ color: stripe }}>
            {secondary}
          </span>
        )}
        <span className="nav-card-row">
          <span className={PILLAR_CLASS[stateV2]} data-testid={`nav-card-state-${issue.id}`} data-state={stateV2}>
            {PILLAR_LABEL[stateV2]}
          </span>
          {issue.estimatedRating ? (
            <span className="nav-card-rating mono">{issue.estimatedRating}%</span>
          ) : null}
          <span className="nav-card-ev mono" title="Evidence items"><Paperclip size={10} weight="bold" /> {evCount}</span>
          {staleCount > 0 && (
            <span
              className="nav-card-stale mono"
              data-testid={`nav-card-stale-${issue.id}`}
              title={`${staleCount} evidence item${staleCount === 1 ? "" : "s"} older than ${STALE_EVIDENCE_MONTHS} months`}
            >
              <WarningCircle size={10} weight="fill" /> {staleCount}
            </span>
          )}
        </span>
      </span>
    </button>
  );
}

function RatingCriteriaSummary({ issue, evidence, waiverCtx }) {
  const tier = getRatingTierSummary(issue);
  const presence = getEvidencePresenceSummary(issue, evidence, waiverCtx);
  if (!tier) return null;
  const targetLabel = tier.target ? `${tier.target}%` : "(not set)";
  return (
    <div
      className="rating-criteria-summary"
      data-testid={`rating-criteria-${issue.id}`}
      style={{
        background: "#F0F9FF",
        border: "1px solid #BAE6FD",
        borderRadius: 4,
        padding: "10px 14px",
        margin: "8px 0",
        fontSize: 13,
        color: "#0C4A6E",
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 4 }}>
        Selected rating target: <span className="mono">{targetLabel}</span>
        {tier.cfrRef && <span style={{ color: "#0369A1", marginLeft: 8, fontWeight: 500 }}>per {tier.cfrRef}</span>}
      </div>
      <div style={{ fontSize: 12, color: "#0369A1", marginBottom: 4, fontStyle: "italic" }}>
        Final rating depends on VA review, C&P exam findings, and the full record.
      </div>
      <div>
        Evidence coverage: <strong>{presence.have} of {presence.total}</strong> proof pillars have evidence attached.
        {tier.higherLevels.length > 0 && !tier.isMaxed && (
          <span> Available higher tiers: {tier.higherLevels.map((r) => `${r}%`).join(" / ")}.</span>
        )}
        {tier.isMaxed && <span> This DC is at the maximum rating.</span>}
      </div>
    </div>
  );
}

function ClaimRiskPanel({ issue, allIssues, evidence, symptoms, waiverCtx }) {
  const [dismissed, setDismissed] = useState({});
  const risks = useMemo(
    () => detectClaimRisks({ issue, allIssues, evidence, symptoms }).filter((r) => !dismissed[r.id]),
    [issue, allIssues, evidence, symptoms, dismissed],
  );
  const analysis = useMemo(
    () => analyzeIssueRisk(issue, allIssues, evidence, waiverCtx || {}),
    [issue, allIssues, evidence, waiverCtx],
  );
  const sevStyle = {
    high: { bg: "#FEF2F2", border: "#FCA5A5", text: "#7F1D1D" },
    med:  { bg: "#FFFBEB", border: "#FCD34D", text: "#78350F" },
    low:  { bg: "#F0F9FF", border: "#BAE6FD", text: "#075985" },
    info: { bg: "#F0FDF4", border: "#BBF7D0", text: "#14532D" },
  };
  const levelTone = {
    Low:      { bg: "#F0FDF4", border: "#BBF7D0", text: "#14532D", chip: "#16a34a" },
    Moderate: { bg: "#FFFBEB", border: "#FCD34D", text: "#78350F", chip: "#d97706" },
    High:     { bg: "#FEF2F2", border: "#FCA5A5", text: "#7F1D1D", chip: "#dc2626" },
  };
  const renderRiskCard = (kind, level, reasons, testid) => {
    const tone = levelTone[level] || levelTone.Low;
    const title = kind === "sc" ? "Service Connection Risk" : "Rating / Severity Risk";
    return (
      <div
        data-testid={testid}
        data-level={level}
        style={{
          background: tone.bg, border: `1px solid ${tone.border}`, borderRadius: 6,
          padding: "10px 12px", color: tone.text, fontSize: 12.5,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <strong style={{ fontSize: 10, letterSpacing: "0.08em" }}>{title.toUpperCase()}</strong>
          <span style={{ background: tone.chip, color: "#fff", padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: "0.05em" }}>{level}</span>
        </div>
        {reasons.length === 0
          ? <div style={{ fontWeight: 600 }}>No issues detected on this side.</div>
          : <ul style={{ margin: 0, paddingLeft: 16 }}>{reasons.map((r) => <li key={r.kind} data-testid={`${testid}-reason-${r.kind}`}>{r.label}</li>)}</ul>
        }
      </div>
    );
  };
  return (
    <div className="claim-risk-panel" data-testid={`claim-risk-${issue.id}`} style={{ margin: "8px 0" }}>
      <div style={{ fontWeight: 800, fontSize: 11, letterSpacing: "0.08em", color: "#475569", marginBottom: 6 }}>
        <ShieldWarning size={12} weight="bold" /> CLAIM RISK CHECK
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
        {renderRiskCard("sc", analysis.serviceConnection.level, analysis.serviceConnection.reasons, `adv-risk-sc-${issue.id}`)}
        {renderRiskCard("rating", analysis.rating.level, analysis.rating.reasons, `adv-risk-rating-${issue.id}`)}
      </div>
      <div
        data-testid={`adv-risk-summary-${issue.id}`}
        style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 6, padding: "10px 12px", marginBottom: 8, fontSize: 12.5, color: "#0F172A" }}
      >
        <div style={{ fontWeight: 700, marginBottom: 4 }}>{analysis.summaryHeadline}</div>
        <div style={{ color: "#475569", lineHeight: 1.5 }}>{analysis.detailMessage}</div>
      </div>
      {risks.length === 0 ? (
        <div
          className="claim-risk-panel--clean"
          data-testid={`claim-risk-clean-${issue.id}`}
          style={{
            background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 4,
            padding: "8px 12px", fontSize: 12, color: "#14532D",
            display: "flex", alignItems: "center", gap: 8,
          }}
        >
          <CheckCircle size={12} weight="fill" /> <strong>No additional red flags detected.</strong>
        </div>
      ) : risks.map((r) => {
        const s = sevStyle[r.severity] || sevStyle.med;
        return (
          <div
            key={r.id}
            data-testid={`risk-${r.id}`}
            style={{
              background: s.bg, border: `1px solid ${s.border}`, borderRadius: 4,
              padding: "8px 12px", marginBottom: 6, color: s.text, fontSize: 12,
              position: "relative",
            }}
          >
            <button
              onClick={() => setDismissed((p) => ({ ...p, [r.id]: true }))}
              data-testid={`risk-dismiss-${r.id}`}
              title="Dismiss"
              style={{
                position: "absolute", top: 6, right: 6, background: "transparent",
                border: "none", cursor: "pointer", color: s.text, opacity: 0.6,
              }}
            >
              <X size={11} weight="bold" />
            </button>
            <div style={{ fontWeight: 800, marginBottom: 2, paddingRight: 18 }}>
              <span style={{ fontSize: 9, letterSpacing: "0.08em", marginRight: 6, opacity: 0.7 }}>
                {r.severity.toUpperCase()}
              </span>
              {r.title}
            </div>
            <div style={{ marginBottom: r.action ? 4 : 0 }}>{r.detail}</div>
            {r.action && <div style={{ fontWeight: 600 }}>→ {r.action}</div>}
          </div>
        );
      })}
    </div>
  );
}

function PasteLanguageButton({ issue, evidence, statements }) {
  const [copied, setCopied] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const text = useMemo(() => buildPasteable526EZLanguage(issue, { evidence, statements }), [issue, evidence, statements]);
  function handleCopy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }
  return (
    <>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "8px 0" }}>
        <button
          className="btn btn--outline btn--sm"
          onClick={handleCopy}
          data-testid={`copy-526ez-${issue.id}`}
          title="Copy paste-ready 526EZ Section IV language"
        >
          <Copy size={12} weight="bold" /> {copied ? "COPIED!" : "COPY 526EZ LANGUAGE"}
        </button>
        <button
          className="btn btn--ghost btn--sm"
          onClick={() => setShowPreview((p) => !p)}
          data-testid={`preview-526ez-${issue.id}`}
        >
          {showPreview ? "Hide preview" : "Preview"}
        </button>
      </div>
      {showPreview && (
        <pre
          data-testid={`preview-526ez-text-${issue.id}`}
          style={{
            background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 4,
            padding: 12, fontSize: 12, lineHeight: 1.5, whiteSpace: "pre-wrap",
            fontFamily: "JetBrains Mono, monospace", margin: "0 0 8px",
            maxHeight: 280, overflow: "auto",
          }}
        >{text}</pre>
      )}
    </>
  );
}

function MultiAddEvidenceModal({ issueId, defaultElement, onClose, onSave }) {
  const [pillar, setPillar] = useState(defaultElement || "current");
  const [text, setText] = useState("");
  const [defaultSource, setDefaultSource] = useState("");
  const [defaultDate, setDefaultDate] = useState(new Date().toISOString().slice(0, 10));

  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);

  function submit() {
    if (!lines.length) return;
    const items = lines.map((title) => ({
      id: `ev-${uid()}`,
      title,
      source: defaultSource,
      fileName: "",
      issueIds: [issueId],
      element: pillar,
      summary: "",
      date: defaultDate,
    }));
    onSave(items);
  }

  return (
    <div className="modal-backdrop" data-testid="multi-evidence-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-panel modal-panel--lg">
        <div className="modal-header">
          <Plus size={16} weight="bold" />
          <span>ADD MULTIPLE EVIDENCE ITEMS</span>
          <button className="modal-close" onClick={onClose}><X size={14} weight="bold" /></button>
        </div>
        <div className="modal-body modal-body--scroll">
          <p style={{ marginTop: 0, fontSize: 13, color: "#475569" }}>
            Paste one evidence title per line. Each line becomes its own evidence item attached to the pillar you pick below. You can edit details after they are added.
          </p>
          <div className="form-grid">
            <label className="field">
              <span className="field-label">PROOF PILLAR</span>
              <select
                className="field-input"
                value={pillar}
                onChange={(e) => setPillar(e.target.value)}
                data-testid="multi-ev-pillar"
              >
                {evidenceElements.map((el) => (
                  <option key={el.id} value={el.id}>{el.label}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field-label">DEFAULT DATE</span>
              <input
                type="date"
                className="field-input mono"
                value={defaultDate}
                onChange={(e) => setDefaultDate(e.target.value)}
                data-testid="multi-ev-date"
              />
            </label>
            <label className="field field--full">
              <span className="field-label">DEFAULT SOURCE (OPTIONAL)</span>
              <input
                className="field-input"
                value={defaultSource}
                onChange={(e) => setDefaultSource(e.target.value)}
                placeholder='e.g., "VA medical record" or "Service treatment record"'
                data-testid="multi-ev-source"
              />
            </label>
            <label className="field field--full">
              <span className="field-label">EVIDENCE TITLES (ONE PER LINE)</span>
              <textarea
                className="field-input"
                rows={9}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={"PTSD diagnosis letter\nVA mental-health note 2025-12-04\nBuddy statement from SSgt Davis\nSTR page 41 - back complaint\n..."}
                data-testid="multi-ev-textarea"
              />
              <span className="field-hint">{lines.length} item{lines.length === 1 ? "" : "s"} will be created.</span>
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn--outline" onClick={onClose}>CANCEL</button>
          <button
            className="btn btn--accent"
            onClick={submit}
            disabled={lines.length === 0}
            data-testid="multi-ev-save"
          >
            <Plus size={14} weight="bold" /> ADD {lines.length || ""} ITEM{lines.length === 1 ? "" : "S"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConditionDetail({
  issue, evidence, allIssues, statements, symptoms,
  updateIssue, deleteIssue,
  addSecondary, addEvidence, addEvidenceBulk, updateEvidence, deleteEvidence,
  existingCodes, onBack, waiverCtx, onSwitchToSimple,
}) {
  const [dbqOpen, setDbqOpen] = useState(false);
  const [prepOpen, setPrepOpen] = useState(false);
  const [secOpen, setSecOpen] = useState(false);
  const [dismissed, setDismissed] = useState({});
  const [view, setView] = useState("pillar"); // "pillar" | "list"
  const [multiAdd, setMultiAdd] = useState(null);  // { issueId, element } | null
  const onAddMulti = (issueId, element) => setMultiAdd({ issueId, element });

  const res = getConditionResources(issue.code, issue.system);
  const suggestions = getSecondarySuggestions(issue.code, existingCodes).filter((s) => !dismissed[s.code]);
  const issueEvidence = evidence.filter((ev) => ev.issueIds?.includes(issue.id));
  const stripe = colorFor(issue.system);
  const sysLabel = bodySystems.find((s) => s.id === issue.system)?.label || issue.system;

  return (
    <div className="panel issue-panel" data-testid={`issue-${issue.id}`} style={{ borderLeft: `4px solid ${stripe}` }}>
      <div className="ct-detail-topbar">
        {onBack && (
          <button className="btn btn--ghost btn--sm ct-back-btn" onClick={onBack} data-testid="ct-back-btn">
            <ArrowLeft size={12} weight="bold" /> Back to list
          </button>
        )}
        {onSwitchToSimple && (
          <button
            type="button"
            className="link-btn ct-back-to-simple"
            onClick={onSwitchToSimple}
            data-testid="ct-back-to-simple"
          >
            Switch to Simple View
          </button>
        )}
      </div>
      <div className="issue-header">
        <div>
          <div className="issue-code mono" data-testid={`issue-code-${issue.id}`}>DC {issue.code}</div>
          <h3 className="issue-title" data-testid={`issue-title-${issue.id}`}>{issue.claimedAs}</h3>
          <div className="issue-system" style={{ color: stripe }}>
            <Tag size={12} weight="bold" />
            {sysLabel}
          </div>
        </div>
        <button className="btn btn--danger btn--sm" onClick={() => deleteIssue(issue.id)} data-testid={`delete-issue-${issue.id}`}>
          <Trash size={14} weight="bold" /> REMOVE
        </button>
      </div>

      <div className="issue-fields">
        <label className="field">
          <span className="field-label">CLAIMED AS</span>
          <input className="field-input" value={issue.claimedAs} onChange={(e) => updateIssue(issue.id, { claimedAs: e.target.value })} data-testid={`issue-claimed-${issue.id}`} />
        </label>
        <label className="field">
          <span className="field-label">THEORY</span>
          <select className="field-input" value={issue.theory} onChange={(e) => updateIssue(issue.id, { theory: e.target.value })} data-testid={`issue-theory-${issue.id}`} title={issue.theory}>
            {claimTheoryOptions.map((t) => <option key={t} value={t} title={t}>{THEORY_SHORT_LABEL[t] || t}</option>)}
          </select>
        </label>
        <label className="field">
          <span className="field-label">EST. RATING</span>
          <select className="field-input mono" value={issue.estimatedRating || 0} onChange={(e) => updateIssue(issue.id, { estimatedRating: Number(e.target.value) })} data-testid={`issue-rating-${issue.id}`}>
            {(() => {
              const cond = conditionLibrary.find((c) => c.code === issue.code);
              const levels = cond?.r?.length ? cond.r : [0,10,20,30,40,50,60,70,80,90,100];
              const allLevels = levels.includes(0) ? levels : [0, ...levels];
              return allLevels.map((r) => <option key={r} value={r}>{`${r}%`}</option>);
            })()}
          </select>
        </label>
        <label className="field">
          <span className="field-label">PRIORITY</span>
          <select className="field-input" value={issue.priority || "Medium"} onChange={(e) => updateIssue(issue.id, { priority: e.target.value })} data-testid={`issue-priority-${issue.id}`}>
            <option>High</option>
            <option>Medium</option>
            <option>Low</option>
          </select>
        </label>
        <label className="field">
          <span className="field-label">SIDE</span>
          <select className="field-input" value={issue.bilateralSide || "Not applicable"} onChange={(e) => updateIssue(issue.id, { bilateralSide: e.target.value })} data-testid={`issue-side-${issue.id}`}>
            <option>Not applicable</option>
            <option>Left</option>
            <option>Right</option>
            <option>Bilateral</option>
          </select>
        </label>
      </div>

      <div className="issue-fields">
        <label className="field field--full">
          <span className="field-label">FUNCTIONAL IMPACT</span>
          <textarea className="field-input field-textarea" value={issue.functionalImpact || ""} onChange={(e) => updateIssue(issue.id, { functionalImpact: e.target.value })} placeholder="How is this condition rated at your estimated rating IAW 38 CFR?" data-testid={`issue-impact-${issue.id}`} />
        </label>
      </div>

      <RatingCriteriaSummary issue={issue} evidence={evidence} waiverCtx={waiverCtx} />
      <ClaimRiskPanel issue={issue} allIssues={allIssues} evidence={evidence} symptoms={symptoms} waiverCtx={waiverCtx} />
      <PasteLanguageButton issue={issue} evidence={evidence} statements={statements} />

      <div className="issue-ref-bar" data-testid={`ref-bar-${issue.id}`}>
        <button className="ref-toggle" onClick={() => setDbqOpen((p) => !p)} data-testid={`toggle-dbq-${issue.id}`}>
          <Stethoscope size={14} weight="bold" />
          <span>RATING BUILDER</span>
          {dbqOpen ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
        </button>
        <button className="ref-toggle" onClick={() => setPrepOpen((p) => !p)} data-testid={`toggle-prep-${issue.id}`}>
          <ClipboardText size={14} weight="bold" />
          <span>C&P PREP</span>
          {prepOpen ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
        </button>
        {suggestions.length > 0 && (
          <button className={`ref-toggle ref-toggle--highlight ${secOpen ? "ref-toggle--active" : ""}`} onClick={() => setSecOpen((p) => !p)} data-testid={`toggle-sec-${issue.id}`}>
            <Lightning size={14} weight="bold" />
            <span>SECONDARIES ({suggestions.length})</span>
            {secOpen ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
          </button>
        )}
      </div>

      {dbqOpen && (
        <div className="issue-ref-content" data-testid={`dbq-content-${issue.id}`}>
          <div className="ref-detail-grid">
            <div className="ref-detail-item">
              <div className="ref-detail-label">DBQ FORM</div>
              <div className="ref-detail-value">{res.dbqName}</div>
              <a href={res.dbqUrl} target="_blank" rel="noopener noreferrer" className="ref-link" data-testid={`dbq-link-${issue.id}`}>
                Download DBQ <ArrowSquareOut size={10} weight="bold" />
              </a>
            </div>
            <div className="ref-detail-item">
              <div className="ref-detail-label">CFR REFERENCE</div>
              <div className="ref-detail-value">{res.cfrRef || "38 CFR Part 4"}</div>
              <a href={res.cfrUrl} target="_blank" rel="noopener noreferrer" className="ref-link" data-testid={`cfr-link-${issue.id}`}>
                View on eCFR <ArrowSquareOut size={10} weight="bold" />
              </a>
            </div>
          </div>
        </div>
      )}

      {prepOpen && res.cpPrep && (
        <div className="issue-prep-content" data-testid={`prep-content-${issue.id}`}>
          <div className="prep-title">C&P EXAM PREPARATION CHECKLIST</div>
          <div className="prep-list">
            {res.cpPrep.map((item, i) => (
              <div key={`prep-${issue.id}-${i}-${item.slice(0,20)}`} className="prep-item">
                <CheckSquare size={14} weight="duotone" className="prep-check" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {secOpen && suggestions.length > 0 && (
        <div className="issue-sec-content" data-testid={`sec-content-${issue.id}`}>
          <div className="sec-title">
            <Link size={14} weight="bold" />
            SECONDARY CONDITION SUGGESTIONS
          </div>
          <div className="sec-desc">These conditions are commonly claimed as secondary to {issue.claimedAs}. Adding them with the "Secondary" theory pre-filled.</div>
          <div className="sec-list">
            {suggestions.map((s) => (
              <div key={s.code} className="sec-item">
                <div className="sec-item-info">
                  <div className="sec-item-name">
                    <span className="mono sec-item-code">DC {s.code}</span>
                    {s.name}
                  </div>
                  <div className="sec-item-reason">{s.reason}</div>
                </div>
                <div className="sec-item-actions">
                  <button className="btn btn--accent btn--sm" onClick={() => addSecondary(s, issue)} data-testid={`add-sec-${issue.id}-${s.code}`}>
                    <Plus size={12} weight="bold" /> ADD
                  </button>
                  <button className="btn btn--ghost btn--sm" onClick={() => setDismissed((p) => ({ ...p, [s.code]: true }))} data-testid={`dismiss-sec-${issue.id}-${s.code}`}>
                    DISMISS
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="ev-section-header" data-testid={`ev-section-header-${issue.id}`}>
        <div className="ev-section-title">
          <Vault size={14} weight="bold" />
          EVIDENCE ({issueEvidence.length})
        </div>
        <div className="ev-view-toggle">
          <button
            className={`ev-view-btn ${view === "pillar" ? "ev-view-btn--active" : ""}`}
            onClick={() => setView("pillar")}
            data-testid={`view-pillar-${issue.id}`}
            title="Group by proof pillar"
          >
            <SquaresFour size={14} weight="bold" /> By pillar
          </button>
          <button
            className={`ev-view-btn ${view === "list" ? "ev-view-btn--active" : ""}`}
            onClick={() => setView("list")}
            data-testid={`view-list-${issue.id}`}
            title="Flat list of all evidence"
          >
            <ListBullets size={14} weight="bold" /> Flat list
          </button>
        </div>
      </div>

      {view === "pillar" ? (
        <div className="ev-pillars" data-testid={`ev-pillars-${issue.id}`}>
          {evidenceElements.map((el) => {
            const items = issueEvidence.filter((ev) => ev.element === el.id);
            const status = computePillarStatus(issue, el.id, evidence, waiverCtx);
            const waiver = el.id === "nexus" ? nexusWaiver(waiverCtx?.workspace, waiverCtx?.master526ez) : { waived: false };
            return (
              <div key={el.id} className="ev-pillar" data-testid={`pillar-${issue.id}-${el.id}`}>
                <div className="ev-pillar-head">
                  <div className="ev-pillar-meta">
                    <div className="ev-pillar-label">
                      {el.label}
                      {waiver.waived && (
                        <span className="ev-pillar-waiver-chip" data-testid={`nexus-waiver-chip-${issue.id}`} title={waiver.reason}>
                          {waiver.shortReason}
                        </span>
                      )}
                    </div>
                    <div className="ev-pillar-help">{waiver.waived ? waiver.reason : el.help}</div>
                  </div>
                  <span
                    className={`ev-pillar-status ${STATUS_CLASS[status]}`}
                    data-testid={`pillar-status-${issue.id}-${el.id}`}
                    title={items.length ? `${items.length} item${items.length === 1 ? "" : "s"} attached` : "No evidence attached"}
                  >
                    {status === "have" ? <CheckCircle size={11} weight="fill" /> : <WarningCircle size={11} weight="fill" />}
                    {" "}{STATUS_LABEL[status]}{items.length > 0 && <span className="ev-pillar-auto"> · {items.length}</span>}
                  </span>
                </div>
                <div className="ev-pillar-body">
                  {items.length === 0 && (
                    <div className="ev-pillar-empty">No evidence attached for this pillar.</div>
                  )}
                  {items.map((ev) => (
                    <EvidenceItemRow key={ev.id} ev={ev} onUpdate={updateEvidence} onDelete={deleteEvidence} />
                  ))}
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <button
                      className="btn btn--outline btn--sm ev-add-btn"
                      onClick={() => addEvidence(issue.id, el.id)}
                      data-testid={`add-evidence-${issue.id}-${el.id}`}
                    >
                      <Paperclip size={12} weight="bold" /> ADD ONE
                    </button>
                    <button
                      className="btn btn--outline btn--sm ev-add-btn"
                      onClick={() => onAddMulti(issue.id, el.id)}
                      data-testid={`add-multi-evidence-${issue.id}-${el.id}`}
                    >
                      <Plus size={12} weight="bold" /> ADD MULTIPLE
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="ev-flat-list" data-testid={`ev-flat-${issue.id}`}>
          {issueEvidence.length === 0 && (
            <div className="ev-pillar-empty">No evidence logged for this condition.</div>
          )}
          {issueEvidence.map((ev) => (
            <EvidenceItemRow key={ev.id} ev={ev} onUpdate={updateEvidence} onDelete={deleteEvidence} showElement />
          ))}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button
              className="btn btn--outline btn--sm ev-add-btn"
              onClick={() => addEvidence(issue.id, "current")}
              data-testid={`add-evidence-flat-${issue.id}`}
            >
              <Paperclip size={12} weight="bold" /> ADD ONE
            </button>
            <button
              className="btn btn--outline btn--sm ev-add-btn"
              onClick={() => onAddMulti(issue.id, "current")}
              data-testid={`add-multi-evidence-flat-${issue.id}`}
            >
              <Plus size={12} weight="bold" /> ADD MULTIPLE
            </button>
          </div>
        </div>
      )}

      <div className="evidence-status-row" data-testid={`evidence-status-${issue.id}`}>
        <div className="evidence-label"><FileText size={11} weight="bold" /> EVIDENCE COVERAGE</div>
        <div className="evidence-grid">
          {evidenceElements.map((el) => {
            const status = computePillarStatus(issue, el.id, evidence, waiverCtx);
            const count = issueEvidence.filter((ev) => ev.element === el.id).length;
            return (
              <div key={el.id} className="evidence-cell" data-testid={`ev-status-${issue.id}-${el.id}`}>
                <span className="evidence-cell-label">{el.label}</span>
                <span className={`evidence-pill mono ${STATUS_CLASS[status]}`}>
                  {STATUS_LABEL[status]}{count > 0 && <span className="ev-pill-count"> · {count}</span>}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      {multiAdd && multiAdd.issueId === issue.id && (
        <MultiAddEvidenceModal
          issueId={multiAdd.issueId}
          defaultElement={multiAdd.element}
          onClose={() => setMultiAdd(null)}
          onSave={(items) => { addEvidenceBulk(items); setMultiAdd(null); }}
        />
      )}
    </div>
  );
}

export default function ClaimTrackerView({
  issues, updateIssue, deleteIssue, addSecondary,
  evidence, setEvidence, deleteEvidence,
  statements = [], symptoms: symptomsProp,
  setActiveView,
  workspace, master526ez,
}) {
  const waiverCtx = useMemo(() => ({ workspace, master526ez }), [workspace, master526ez]);
  // Default to `null` (no selection) on mount. On desktop the user gets the empty-state pane
  // and picks from the navigator; on mobile this means they land on the list (not in detail
  // view), which is the expected nav pattern. Auto-selecting the first issue used to fight
  // the mobile "Back to list" flow.
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("alpha"); // alpha (default) | created | priority | rating | readiness | system
  const [groupBy, setGroupBy] = useState("none");  // none | system | priority
  const [symptoms, setSymptoms] = useState(symptomsProp || []);

  // Simple View is the default. Advanced View reveals the legacy full-detail
  // grid (rating math, secondaries panel, inline evidence forms, etc.).
  // Persist the toggle per-browser so power users don't have to flip it
  // every session.
  const [simpleView, setSimpleView] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem("claimops-tracker-simple") !== "0";
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("claimops-tracker-simple", simpleView ? "1" : "0");
    }
  }, [simpleView]);

  // SimpleConditionDetail emits window events for cross-component selection
  // and for opening the existing multi-add modal against a specific pillar.
  useEffect(() => {
    function onSelect(e) {
      const id = e?.detail?.issueId;
      if (id) setSelectedId(id);
    }
    function onAdd(e) {
      const { issueId, elementId } = e?.detail || {};
      if (issueId && elementId) setMultiAddTop({ issueId, element: elementId });
    }
    window.addEventListener("claimops:select-condition", onSelect);
    window.addEventListener("claimops:open-evidence-add", onAdd);
    return () => {
      window.removeEventListener("claimops:select-condition", onSelect);
      window.removeEventListener("claimops:open-evidence-add", onAdd);
    };
  }, []);
  const [multiAddTop, setMultiAddTop] = useState(null); // {issueId, element} | null

  // Fetch symptoms once for the claim-risk panel (best-effort; failures are silent).
  useEffect(() => {
    if (symptomsProp) return;
    let cancelled = false;
    listSymptoms().then((data) => { if (!cancelled) setSymptoms(data || []); });
    return () => { cancelled = true; };
  }, [symptomsProp]);

  // Reset selection when issues change in a way that makes the current selection stale:
  //  - All issues were deleted → drop selection.
  //  - The currently selected issue was deleted from somewhere else → drop selection so
  //    the user can pick a different one (or land on the "Select a condition" pane).
  // We deliberately do NOT auto-select issues[0] here, because that would defeat the
  // mobile "Back to list" button (which explicitly nulls selectedId to show the navigator).
  useEffect(() => {
    if (issues.length === 0) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    if (selectedId && !issues.some((i) => i.id === selectedId)) {
      setSelectedId(null);
    }
  }, [issues, selectedId]);

  const existingCodes = issues.map((i) => i.code);

  function addEvidence(issueId, elementId) {
    setEvidence((prev) => [
      {
        id: `ev-${uid()}`,
        title: "",
        source: "",
        fileName: "",
        issueIds: [issueId],
        element: elementId,
        summary: "",
        date: new Date().toISOString().slice(0, 10),
      },
      ...prev,
    ]);
  }

  function addEvidenceBulk(items) {
    setEvidence((prev) => [...items, ...prev]);
  }

  function updateEvidence(id, patch) {
    setEvidence((prev) => prev.map((ev) => (ev.id === id ? { ...ev, ...patch } : ev)));
  }

  function deleteIssueAndSelect(issueId) {
    deleteIssue(issueId);
    if (selectedId === issueId) {
      const remaining = issues.filter((i) => i.id !== issueId);
      setSelectedId(remaining[0]?.id || null);
    }
  }

  // -- Filter + sort the issue list for the navigator
  const filteredSorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? issues.filter((i) =>
          (i.claimedAs || "").toLowerCase().includes(q) ||
          (i.code || "").toLowerCase().includes(q) ||
          (bodySystems.find((s) => s.id === i.system)?.label || i.system || "").toLowerCase().includes(q) ||
          (i.theory || "").toLowerCase().includes(q)
        )
      : [...issues];
    const sorters = {
      created: () => 0, // preserve insertion order (issues are passed in order)
      priority: (a, b) => (priorityRank(a.priority) - priorityRank(b.priority)),
      rating: (a, b) => ((b.estimatedRating || 0) - (a.estimatedRating || 0)),
      readiness: (a, b) => (issueReadinessPct(b, evidence, waiverCtx) - issueReadinessPct(a, evidence, waiverCtx)),
      system: (a, b) => (a.system || "").localeCompare(b.system || ""),
      alpha: (a, b) => (a.claimedAs || "").localeCompare(b.claimedAs || ""),
    };
    filtered.sort(sorters[sortBy] || sorters.created);
    return filtered;
  }, [issues, search, sortBy, evidence, waiverCtx]);

  // -- Group for navigator (if groupBy != "none")
  const grouped = useMemo(() => {
    if (groupBy === "none") return [{ key: "all", label: null, items: filteredSorted }];
    if (groupBy === "system") {
      const map = new Map();
      filteredSorted.forEach((i) => {
        const k = i.system || "other";
        if (!map.has(k)) map.set(k, []);
        map.get(k).push(i);
      });
      // Stable order: follow bodySystems order, then any extras
      const order = bodySystems.map((s) => s.id);
      const sortedKeys = [...map.keys()].sort((a, b) => {
        const ia = order.indexOf(a), ib = order.indexOf(b);
        if (ia === -1 && ib === -1) return a.localeCompare(b);
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
      });
      return sortedKeys.map((k) => ({
        key: k,
        label: bodySystems.find((s) => s.id === k)?.label || k,
        color: colorFor(k),
        items: map.get(k),
      }));
    }
    if (groupBy === "priority") {
      const map = { High: [], Medium: [], Low: [] };
      filteredSorted.forEach((i) => { (map[i.priority || "Medium"] ||= []).push(i); });
      return ["High", "Medium", "Low"]
        .filter((k) => map[k] && map[k].length)
        .map((k) => ({ key: k, label: `${k} priority`, items: map[k] }));
    }
    return [{ key: "all", label: null, items: filteredSorted }];
  }, [filteredSorted, groupBy]);

  const selectedIssue = issues.find((i) => i.id === selectedId) || null;

  // Aggregate stats for the toolbar
  const stats = useMemo(() => {
    const totalEv = evidence.length;
    const highPriorityCount = issues.filter((i) => i.priority === "High").length;
    const ready = issues.length === 0 ? 0 : Math.round(
      issues.reduce((sum, i) => sum + issueReadinessPct(i, evidence, waiverCtx), 0) / issues.length
    );
    return { totalEv, highPriorityCount, ready };
  }, [issues, evidence, waiverCtx]);

  return (
    <div className="view-grid" data-testid="claim-tracker-view">
      <div className="panel">
        <div className="panel-header">
          <Tag size={18} weight="bold" />
          <span>CLAIM TRACKER</span>
          <span className="header-count mono">{issues.length}</span>
          <span className="header-sub mono ml-auto">{stats.totalEv} evidence • {stats.ready}% ready • {stats.highPriorityCount} high-pri</span>
        </div>
        <div className="panel-body">
          <p className="panel-desc">
            Pick a condition on the left to edit it on the right. Each card shows its body-system color, priority, est. rating, evidence count, and a readiness bar. Use search, sort, or grouping to find conditions fast.
          </p>
        </div>
      </div>

      {issues.length === 0 ? (
        <div className="panel">
          <div className="panel-body">
            <div className="empty-state" data-testid="no-issues">
              <p>No claimed conditions yet. Add conditions from the Condition Library.</p>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Commonly co-claimed secondaries — moved here from Command Center so it lives
              next to the actual claim management workflow. */}
          {addSecondary && (
            <CoOccurrenceHints
              issues={issues}
              addSecondary={addSecondary}
              setActiveView={setActiveView}
            />
          )}
          <div className={`ct-shell${selectedId ? " ct-shell--detail-open" : ""}`} data-testid="ct-shell">
          <aside className="ct-sidebar" data-testid="ct-sidebar">
            <div className="ct-toolbar">
              <div className="ct-search-wrap">
                <MagnifyingGlass size={13} weight="bold" className="ct-search-icon" />
                <input
                  className="field-input ct-search-input"
                  placeholder="Search code, name, system..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="ct-search-input"
                />
              </div>
              <div className="ct-toolbar-row">
                <label className="ct-toolbar-select" title="Sort by">
                  <ArrowsDownUp size={11} weight="bold" />
                  <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="ct-sort-select">
                    <option value="alpha">Alphabetical (default)</option>
                    <option value="created">Added (newest first)</option>
                    <option value="priority">Priority (H→L)</option>
                    <option value="rating">Rating (high→low)</option>
                    <option value="readiness">Readiness (high→low)</option>
                    <option value="system">Body system</option>
                  </select>
                </label>
                <label className="ct-toolbar-select" title="Group by">
                  <Funnel size={11} weight="bold" />
                  <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} data-testid="ct-groupby-select">
                    <option value="none">No grouping</option>
                    <option value="system">Group by system</option>
                    <option value="priority">Group by priority</option>
                  </select>
                </label>
              </div>
              <div className="ct-sidebar-count mono">
                {filteredSorted.length === issues.length
                  ? `${issues.length} conditions`
                  : `${filteredSorted.length} of ${issues.length}`}
                <span className="ct-view-toggle" role="tablist" data-testid="ct-view-toggle">
                  <button
                    type="button"
                    className={`ct-view-toggle-btn ${simpleView ? "ct-view-toggle-btn--active" : ""}`}
                    role="tab"
                    aria-selected={simpleView}
                    onClick={() => setSimpleView(true)}
                    data-testid="ct-view-simple"
                    title="Simple view — recommended"
                  >
                    Simple
                  </button>
                  <button
                    type="button"
                    className={`ct-view-toggle-btn ${!simpleView ? "ct-view-toggle-btn--active" : ""}`}
                    role="tab"
                    aria-selected={!simpleView}
                    onClick={() => setSimpleView(false)}
                    data-testid="ct-view-advanced"
                    title="Advanced view — full power-user layout"
                  >
                    Advanced
                  </button>
                </span>
              </div>
            </div>

            <div className="ct-nav-list" data-testid="ct-nav-list">
              {filteredSorted.length === 0 && (
                <div className="ct-nav-empty" data-testid="ct-nav-empty">No conditions match your search.</div>
              )}
              {grouped.map((g) => (
                <React.Fragment key={g.key}>
                  {g.label && (
                    <div
                      className="ct-nav-group-head"
                      style={g.color ? { borderLeftColor: g.color, color: g.color } : undefined}
                      data-testid={`ct-group-${g.key}`}
                    >
                      {g.label}
                      <span className="ct-nav-group-count mono">{g.items.length}</span>
                    </div>
                  )}
                  {g.items.map((issue) => (
                    <NavigatorCard
                      key={issue.id}
                      issue={issue}
                      evidence={evidence}
                      isActive={issue.id === selectedId}
                      onClick={() => setSelectedId(issue.id)}
                      waiverCtx={waiverCtx}
                    />
                  ))}
                </React.Fragment>
              ))}
            </div>
          </aside>

          <main className="ct-main" data-testid="ct-main">
            {selectedIssue ? (
              simpleView ? (
                <SimpleConditionDetail
                  key={`simple-${selectedIssue.id}`}
                  issue={selectedIssue}
                  allIssues={issues}
                  evidence={evidence}
                  waiverCtx={waiverCtx}
                  updateIssue={updateIssue}
                  deleteIssue={deleteIssueAndSelect}
                  addEvidence={addEvidence}
                  deleteEvidence={deleteEvidence}
                  setActiveView={setActiveView}
                  onBack={() => setSelectedId(null)}
                  onSwitchToAdvanced={() => setSimpleView(false)}
                />
              ) : (
                <ConditionDetail
                  key={selectedIssue.id}
                  issue={selectedIssue}
                  evidence={evidence}
                  allIssues={issues}
                  statements={statements}
                  symptoms={symptoms}
                  updateIssue={updateIssue}
                  deleteIssue={deleteIssueAndSelect}
                  addSecondary={addSecondary}
                  addEvidence={addEvidence}
                  addEvidenceBulk={addEvidenceBulk}
                  updateEvidence={updateEvidence}
                  deleteEvidence={deleteEvidence}
                  existingCodes={existingCodes}
                  onBack={() => setSelectedId(null)}
                  onSwitchToSimple={() => setSimpleView(true)}
                  waiverCtx={waiverCtx}
                />
              )
            ) : (
              <div className="panel">
                <div className="panel-body">
                  <div className="ct-empty-detail" data-testid="ct-empty-detail">
                    <Compass size={28} weight="duotone" />
                    <h3>Select a condition</h3>
                    <p>Pick a condition from the left to view and edit its details, evidence, and proof pillars.</p>
                  </div>
                </div>
              </div>
            )}
          </main>
        </div>
        </>
      )}
      {multiAddTop && (
        <MultiAddEvidenceModal
          issueId={multiAddTop.issueId}
          defaultElement={multiAddTop.element}
          onClose={() => setMultiAddTop(null)}
          onSave={(items) => { addEvidenceBulk(items); setMultiAddTop(null); }}
        />
      )}
    </div>
  );
}
